import axios from 'axios';

import api from '../httpClient';

type UploadOptions = {
  visibility?: 'public' | 'private';
  folder?: string;
  hash?: string;
  mediaType?: 'image' | 'video' | 'pdf' | 'audio' | 'other';
};

type UploadResponse = {
  key: string;
  url?: string;
  assetId?: string;
  mediaType?: string;
  mimeType?: string;
  size?: number;
  sha256?: string;
  originalFilename?: string;
  duplicate?: boolean;
};

const normalizeUploadResponse = (data: any): UploadResponse => ({
  key: data.key,
  url: data.url,
  assetId: data.asset_id,
  mediaType: data.media_type,
  mimeType: data.mime_type,
  size: data.size,
  sha256: data.sha256,
  originalFilename: data.original_filename,
  duplicate: data.duplicate,
});

const uploadViaCanonicalMedia = async (file: File, options?: UploadOptions): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.visibility) {
    formData.append('visibility', options.visibility);
  }
  if (options?.folder) {
    formData.append('folder', options.folder);
  }
  if (options?.hash) {
    formData.append('hash', options.hash);
  }
  if (options?.mediaType) {
    formData.append('media_type', options.mediaType);
  }

  const response = await api.post('/api/media/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return normalizeUploadResponse(response.data);
};

export const videos = {
  getVideoUrl: async (videoKey: string): Promise<string> => {
    try {
      const response = await api.get(`/api/videos/signed-url/${encodeURIComponent(videoKey)}/`, {
        headers: {
          Accept: 'application/json',
        },
        validateStatus: (status) => status < 500,
      });

      if (!response.data || !response.data.url) {
        console.error('Invalid response data:', response.data);
        throw new Error('Invalid video URL response');
      }

      return response.data.url;
    } catch (error) {
      console.error('Error fetching video URL:', error);
      if (axios.isAxiosError(error)) {
        const rawResponse = error.response?.data;
        console.error('Raw error response:', rawResponse);
        console.error('Status:', error.response?.status);
        console.error('Headers:', error.response?.headers);
        console.error('Request URL:', error.config?.url);
        console.error('Base URL:', error.config?.baseURL);
      }
      throw new Error(error instanceof Error ? error.message : 'Server returned invalid JSON');
    }
  },
  getSignedUrl: async (key: string) =>
    api.get(`/api/videos/signed-url/${encodeURIComponent(key)}/`).then((response) => response.data.url),
};

export const pdfs = {
  getSignedUrl: async (key: string): Promise<string> => {
    const response = await api.get(`/api/pdfs/signed-url/${key}/`);
    return response.data.url;
  },
  upload: async (file: File): Promise<string> => {
    const response = await uploadViaCanonicalMedia(file, { mediaType: 'pdf' });
    return response.key;
  },
};

export const images = {
  getSignedUrl: async (key: string): Promise<string> => {
    const encodedKey = encodeURIComponent(key);
    const response = await api.get(`/api/images/signed-url/${encodedKey}/`);
    return response.data.url;
  },
  upload: async (
    file: File,
    options?: { visibility?: 'public' | 'private'; folder?: string },
  ): Promise<string> => {
    const response = await uploadViaCanonicalMedia(file, {
      visibility: options?.visibility,
      folder: options?.folder,
      mediaType: 'image',
    });
    return response.key;
  },
  uploadWithMetadata: async (
    file: File,
    options?: { visibility?: 'public' | 'private'; folder?: string },
  ): Promise<{
    key: string;
    assetId?: string;
    mediaType?: string;
    mimeType?: string;
    size?: number;
    sha256?: string;
    originalFilename?: string;
  }> => {
    return uploadViaCanonicalMedia(file, {
      visibility: options?.visibility,
      folder: options?.folder,
      mediaType: 'image',
    });
  },
};

export const media = {
  getSignedUrl: async (key: string): Promise<string> => {
    const encodedKey = encodeURIComponent(key);
    const response = await api.get(`/api/media/signed-url/${encodedKey}/`);
    return response.data.url;
  },
  lookup: async (
    hash: string,
  ): Promise<{ found: boolean; assetId?: string; key?: string; mediaType?: string; mimeType?: string; size?: number; originalFilename?: string }> => {
    const response = await api.post('/api/media/lookup/', { hash });
    return {
      found: !!response.data.found,
      assetId: response.data.asset_id,
      key: response.data.key,
      mediaType: response.data.media_type,
      mimeType: response.data.mime_type,
      size: response.data.size,
      originalFilename: response.data.original_filename,
    };
  },
  upload: async (
    file: File,
    options?: { visibility?: 'public' | 'private'; folder?: string; hash?: string },
  ): Promise<UploadResponse> =>
    uploadViaCanonicalMedia(file, {
      visibility: options?.visibility,
      folder: options?.folder,
      hash: options?.hash,
    }),
};
